import React from "react";
import { Dashboard } from "./Molecules/Dashboard/Dashboard";
import Design from "./Molecules/Design/Design";
import ChooseMachine from "./Molecules/ChooseMachine/ChooseMachine";
import { FinalParameters } from "./Molecules/FinalParameters/FinalParameters";
import { BlindTestDetailsList } from "./Molecules/BlindTesting/BlindTestDetailsList";

import {
  BrowserRouter as Router,
  Route,
  Routes,
} from 'react-router-dom';
import { useCountrySync } from '../hooks/useCountrySync';

import { ProductDesigns } from "./Molecules/ProductDesigns/ProductDesigns";
import { ProductDesignForm } from "./Molecules/ProductDesigns/ProductDesignForm";
import { ModelerOverview, ModelDataSampleList, ModelDataSampleForm } from "./Molecules/Modeler";
import { Roles } from "../Models/Enums";
import { PHEPlateTypeManagement } from "./Molecules/Admin/PHEPlateTypeManagement";
import { Admin } from "./Molecules/Admin/Admin";
import { BlindTestOverview } from "./Molecules/BlindTesting/BlindTestOverview";
import { About } from "./Molecules/About/About";
import { UnitConfigurationEditor } from "./Molecules/Admin/UnitConfiguration/UnitConfigurationEditor";
import { UnitPropertySpecificationEditor } from "./Molecules/Admin/UnitConfiguration/UnitPropertySpecificationEditor";
import { PropertyRangeSelection } from "./Molecules/Admin/PropertyRangeSelection";

import { Navbar } from "./Molecules/NavigationBar/Navbar";
import { SMESidebar } from "./Molecules/Sidebar/SMESidebar";
import { Sidebar } from "./Molecules/Sidebar/Sidebar";
import { BlindTestDashboard } from "./Molecules/SME/BlindTestDashboard/BlindTestDashboard";
import { BlindTestList } from "./Molecules/SME/BlindTests/BlindTestList";
import { DesignSessionReviewForm } from "./Molecules/SME/DesignSessionReview/DesignSessionReviewForm";
import { UnitConfigurationManagement } from "./Molecules/Admin/UnitConfiguration/UnitConfigurationManagement";

// Material UI imports
import {
  Box,
  useTheme,
} from '@mui/material';

// Import Error Boundaries
import { RouteErrorBoundary, FeatureErrorBoundary } from "./ErrorBoundaries";
import { hasRole } from "../Services/Global";

export interface PagesProps {
  isSme: boolean;
  sellerFeatureActive: boolean;
  modelerFeatureActive: boolean;
  showConditionalFields: boolean;
  isModeler: boolean;
  isBlindtester: boolean;
}

export const Pages: React.FC<PagesProps> = (props) => {

  const theme = useTheme();

  // Styles object for Pages component
  const styles = {
    mainLayout: {
      display: 'flex',
      flexDirection: 'row',
      height: 'calc(100vh - 60px)', // Subtract navbar height
      overflow: 'hidden'
    },
    contentArea: {
      flex: 1,
      overflow: 'auto',
      height: '100%',
      boxShadow: theme.shadows[8],
      backgroundColor: theme.palette.background.paper
    }
  };

  let starRedirection: JSX.Element = <></>;
  if (hasRole(...[Roles[Roles.SME]])) {
    starRedirection = <BlindTestDashboard isModeler={props.isModeler} isSme={props.isSme} />;
  }
  else if (hasRole(...[Roles[Roles.Modeler]])) {
    starRedirection = <ModelerOverview />;
  }
  else if (hasRole(...[Roles[Roles.BlindTestUser]])) {
    starRedirection = <BlindTestOverview />;
  }
  else {
    starRedirection = <About />;
  }

  // Inner component that uses Router context
  const PagesContent: React.FC = () => {
    // Synchronize country parameter from URL with localStorage
    // This must be inside Router context
    useCountrySync();

    return (
      <>
      <Navbar 
        sellerFeatureActive={props.sellerFeatureActive}
        modelerFeatureActive={props.modelerFeatureActive}
      />

      <Box sx={styles.mainLayout}>
        {
          props.isSme === true ?
            <SMESidebar />
            :
            <Sidebar />
        }
        <Box sx={styles.contentArea}>
          <Routes>
            {/* Root path - home page based on user role */}
            <Route path="/" element={starRedirection} />
            <Route path="*" element={starRedirection} />
            {
              //SME Only roles
              (hasRole(...[Roles[Roles.SME]]) || process.env.NODE_ENV === 'development') &&
              <>
                <Route path="/blindtests/dashboard" element={
                  <RouteErrorBoundary routeName="Blind Tests Dashboard">
                    <BlindTestDashboard isModeler={props.isModeler} isSme={props.isSme} />
                  </RouteErrorBoundary>
                } />
                <Route path="/admin" element={
                  <RouteErrorBoundary routeName="Admin">
                    <Admin />
                  </RouteErrorBoundary>
                } />
                <Route path="/blindtests/:samplingId" element={
                  <RouteErrorBoundary routeName="Blind Test List">
                    <BlindTestList />
                  </RouteErrorBoundary>
                } />
                <Route path="/DesignSessionReviewForm" element={
                  <RouteErrorBoundary routeName="Design Session Review">
                    <DesignSessionReviewForm showConditionalFields={props.showConditionalFields} />
                  </RouteErrorBoundary>
                } />
              </>
            }
            {
              //Modeler Only roles
              (hasRole(...[Roles[Roles.Modeler]]) || process.env.NODE_ENV === 'development') &&
              <>
                <Route path="/blindtests/dashboard" element={
                  <RouteErrorBoundary routeName="Blind Tests Dashboard">
                    <BlindTestDashboard isModeler={props.isModeler} isSme={props.isSme} />
                  </RouteErrorBoundary>
                } />
                <Route path="/blindtests/:samplingId" element={
                  <RouteErrorBoundary routeName="Blind Test List">
                    <BlindTestList />
                  </RouteErrorBoundary>
                } />
                <Route path="/DesignSessionReviewForm" element={
                  <RouteErrorBoundary routeName="Design Session Review">
                    <DesignSessionReviewForm showConditionalFields={props.showConditionalFields} />
                  </RouteErrorBoundary>
                } />
                <Route path="/admin/UnitConfigurationManagement" element={
                  <RouteErrorBoundary routeName="Unit Configuration Management">
                    <UnitConfigurationManagement />
                  </RouteErrorBoundary>
                } />
                <Route path="/admin/UnitConfigurationManagement/unitconfigurationeditor" element={
                  <RouteErrorBoundary routeName="Unit Configuration Editor">
                    <UnitConfigurationEditor />
                  </RouteErrorBoundary>
                } />
                <Route path="/admin/unitconfigurationmanagement/propertyspecificationeditor/:i/:si/:a/:sa/:ps/:pl/:alp/:sc" element={
                  <RouteErrorBoundary routeName="Property Specification Editor">
                    <UnitPropertySpecificationEditor />
                  </RouteErrorBoundary>
                } />
                <Route path="/admin/unitconfigurationmanagement/propertyrangespecificationeditor/:i/:si/:a/:sa/:ps/:pl/:alp/:sc/:ip" element={
                  <RouteErrorBoundary routeName="Property Range Selection">
                    <PropertyRangeSelection />
                  </RouteErrorBoundary>
                } />
                <Route path="/admin/PHEPlateTypeManagement" element={
                  <RouteErrorBoundary routeName="PHE Plate Type Management">
                    <PHEPlateTypeManagement />
                  </RouteErrorBoundary>
                } />
              </>
            }
            {
              //Seller and Modeler roles 
              ((hasRole(...[Roles[Roles.Seller]]) || hasRole(...[Roles[Roles.Modeler]])) || process.env.NODE_ENV === 'development') &&
              <>
                <Route path="/dashboard" element={
                  <RouteErrorBoundary routeName="Dashboard">
                    <Dashboard />
                  </RouteErrorBoundary>
                } />
                <Route path="/design" element={
                  <RouteErrorBoundary routeName="Design">
                    <Design />
                  </RouteErrorBoundary>
                } />
                <Route path="/choosemachine" element={
                  <RouteErrorBoundary routeName="Choose Machine">
                    <ChooseMachine />
                  </RouteErrorBoundary>
                } />
                <Route path="/productdesigns" element={
                  <RouteErrorBoundary routeName="Product Designs">
                    <ProductDesigns />
                  </RouteErrorBoundary>
                } />
                <Route path="/finalparameters" element={
                  <RouteErrorBoundary routeName="Final Parameters">
                    <FinalParameters />
                  </RouteErrorBoundary>
                } />
              </>
            }
            {
              //BlindTestUser roles
              (hasRole(...[Roles[Roles.BlindTestUser]]) || process.env.NODE_ENV === 'development') &&
              <>
                <Route path="/blindtests/overview" element={
                  <RouteErrorBoundary routeName="Blind Tests Overview">
                    <BlindTestDetailsList />
                  </RouteErrorBoundary>
                } />
                <Route path="/blindtests" element={
                  <RouteErrorBoundary routeName="Blind Tests">
                    <BlindTestOverview />
                  </RouteErrorBoundary>
                } />
              </>
            }
            {
              (props.modelerFeatureActive || process.env.NODE_ENV === 'development') &&
              <>
                <Route path="/modeling/dashboard" element={
                  <FeatureErrorBoundary featureName="Modeling Dashboard" allowDismiss={false}>
                    <ModelerOverview />
                  </FeatureErrorBoundary>
                } />
                <Route path="/modeling/:samplingId/datasamples" element={
                  <FeatureErrorBoundary featureName="Model Data Samples" allowDismiss={false}>
                    <ModelDataSampleList />
                  </FeatureErrorBoundary>
                } />
                <Route path="/modeling/:samplingId/datasamples/:dataSampleId/review" element={
                  <FeatureErrorBoundary featureName="Model Data Sample Review" allowDismiss={false}>
                    <ModelDataSampleForm />
                  </FeatureErrorBoundary>
                } />
              </>
            }

            {/* Support landing on a product design page by URL + query navigation */}
            <Route path='/productdesign' element={
              <RouteErrorBoundary routeName="Product Design">
                <ProductDesignForm showConditionalFields={props.showConditionalFields} />
              </RouteErrorBoundary>
            } />
            <Route path='/about' element={
              <RouteErrorBoundary routeName="Home">
                <About />
              </RouteErrorBoundary>
            } />

            <Route path="/admin" element={
              <RouteErrorBoundary routeName="Admin">
                <Admin />
              </RouteErrorBoundary>
            } />
            {
              props.isSme === true ?
                <Route path="/login/callback" element={
                  <RouteErrorBoundary routeName="Login Callback (SME)">
                    <BlindTestDashboard isModeler={props.isModeler} isSme={props.isSme} />
                  </RouteErrorBoundary>
                } />
                :
                <Route path="/login/callback" element={
                  <RouteErrorBoundary routeName="Login Callback">
                    <Dashboard />
                  </RouteErrorBoundary>
                } />
            }
          </Routes>
        </Box>
      </Box>
      </>
    );
  };

  return (
    <Router>
      <PagesContent />
    </Router>
  )
}

export default Pages; 